import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl-next';

import type { ButtonProps } from '@atlaskit/button/new';
import HomeIcon from '@atlaskit/icon/core/home';
import LogInIcon from '@atlaskit/icon/core/log-in';

import { NOT_FOUND_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { withRoutesContext } from '@confluence/route-manager/entry-points/withRoutesContext.ts';
import { HOME, SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getLoginLink } from '@confluence/login-if-anonymous/entry-points/getLoginLink.ts';

import notFoundImage from './NotFoundIcon.svg';
import { ErrorPageComponent } from './ErrorPageComponent.tsx';
import { NOT_FOUND_PAGE_LOAD } from './perf.config.ts';

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'NOT_FOUND',
};

export const NotFound: React.FC<{ secondaryButtonProps: ButtonProps }> = withRoutesContext(
	({ push, toUrl, secondaryButtonProps }) => {
		const { homepageUri } = useContext(SPAViewContext);
		const primaryButtonUrl = ['/spa.action', '', 'homepageUri'].includes(homepageUri)
			? toUrl(HOME.name)
			: toUrl(SPACE_OVERVIEW.name, { href: `${CONTEXT_PATH}${homepageUri}` });

		const { isLoggedIn } = useSessionData();
		const logInButtonProps = {
			children: (
				<FormattedMessage
					id="not-found.link.to.log-in"
					defaultMessage="Log in"
					description="Link button to log in"
				/>
			),
			onClick: () => push(getLoginLink()),
			iconBefore: LogInIcon,
		};
		const secondaryButton =
			!isLoggedIn && !secondaryButtonProps ? logInButtonProps : secondaryButtonProps;

		return (
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<ExperienceStart name={NOT_FOUND_EXPERIENCE} />
				<div data-testid="confluence-not-found">
					<ErrorPageComponent
						errorImage={notFoundImage}
						errorTitle={
							<FormattedMessage
								id="not-found.header"
								// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
								// eslint-disable-next-line no-restricted-syntax
								defaultMessage="We couldn't find what you're looking for"
								description="Header line of a 404 Not Found page for any content type"
							/>
						}
						errorMessage={
							<FormattedMessage
								id="not-found.description"
								// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
								// eslint-disable-next-line no-restricted-syntax
								defaultMessage="It may have been deleted, the URL could have a typo in it, or you may need to log in to view it."
								description="Line below the 404 Not Found header line"
							/>
						}
						primaryButtonProps={{
							children: (
								<FormattedMessage
									id="not-found.link.to.homepage"
									defaultMessage="Go home"
									description="A button that links to website's homepage"
								/>
							),
							iconBefore: HomeIcon,
							onClick: () => push(primaryButtonUrl),
						}}
						secondaryButtonProps={secondaryButton}
					/>
					<PageLoadEnd metric={NOT_FOUND_PAGE_LOAD} customData={CUSTOM_ATTRIBUTES} />
					<ExperienceSuccess name={NOT_FOUND_EXPERIENCE} />
				</div>
			</ErrorBoundary>
		);
	},
);

NotFound.displayName = 'NotFound';
